import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import {
  fetchProduct,
  fetchProducts,
  updateProduct,
  createProduct,
  destroyProduct,
} from "../../actions/mine_craft/productsActions";
import { updateProducts } from "../../actions/mine_craft/bundlesActions";

export interface ProductInterface {
  id: number;
  bundle_id: number;
  productid: string;
  price: number;
  amount: number;
  created_at?: string;
  updated_at?: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;

  newParams: ProductInterface | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null,
};

const adaptor = createEntityAdapter<ProductInterface>();

const slice = createSlice({
  name: 'products',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchProducts.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.products;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchProduct.fulfilled, (state, action) => {
        const product = Object.values(action.payload.product)[0];
        state.entities[product.id] = product;
      })

      // #create
      .addCase(createProduct.fulfilled, (state, action) => {
        const product = Object.values(action.payload.product)[0];
        state.entities[product.id] = product;
        state.ids.unshift(product.id);
      })
      .addCase(createProduct.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #update
      .addCase(updateProduct.fulfilled, (state, action) => {
        const product = Object.values(action.payload.product)[0];
        state.entities[product.id] = product;
      })
      .addCase(updateProduct.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #updateProducts — bulk-replace the catalog for a bundle. The
      // backend echoes the reconciled product list back; replace the
      // store with the new collection (scoped to the bundle being saved).
      .addCase(updateProducts.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.products;
        state.ids = action.payload.ids;
      })
      .addCase(updateProducts.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #destroy
      .addCase(destroyProduct.fulfilled, (state, action) => {
        adaptor.removeOne(state, action.payload.id);
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setNewParams,
  setNewError,
  setEditError
} = slice.actions;

export const {
  selectById: selectProductById,
  selectIds: selectProductIds,
  selectEntities: selectProductEntities,
  selectAll: selectAllProducts,
  selectTotal: selectTotalProducts
} = adaptor.getSelectors<RootState>(state => state.mineCraft.products);
